import {
  CheckStatus,
  EntityRefUtil,
  formatDateWithUserTimezone,
  GetWorkOrdersRequestDTO,
  GetWorkOrdersResponseDTO,
  IssueStatus,
  TagEntity,
  WORK_ORDER_USERS_COLUMN_HEADER,
  WorkOrderPriority,
  WorkOrderStatus,
} from '@gearbelt/global';
import { AccessorKeyColumnDef, createColumnHelper } from '@tanstack/react-table';
import { match } from 'ts-pattern';

import WarningIcon from '@/components/icons/warning';
import { ASSET_SECTION_LABEL, createAssetTagsColumn } from '@/components/table-page/columns/asset-tags-column';
import { createTagsColumn } from '@/components/table-page/columns/tags-column';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { UserAvatarLabel } from '@/components/users/user-avatar-label';
import { TaskProgress } from '@/components/work-orders/task-progress';
import { cn, initials } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { getAvatarBackgroundColorClass } from '@/utils/avatar';
import { getWorkOrderPriorityColorClass } from '@/utils/work-order';

import { renderCheckBadges, renderIssueBadges } from './work-order-badge-renderers';

const columnHelper = createColumnHelper<GetWorkOrdersResponseDTO['data'][number]>();

export const createWorkOrdersTableColumns = () => {
  const userTimezone = useUserStore.getState().user?.timezone;
  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2">
            <Checkbox
              checked={table.getIsAllPageRowsSelected()}
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label="Select all"
            />
          </label>
        </div>
      ),
      cell: ({ row }) => (
        <div className="flex w-full justify-center">
          <label className="-m-2 p-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
          </label>
        </div>
      ),
      meta: {
        cellClassName: 'w-[40px] min-w-[40px] max-w-[40px]',
      },
      enableSorting: false,
      enableHiding: false,
    }),
    columnHelper.accessor('priority', {
      header: 'Priority',
      enableSorting: true,
      cell: ({ getValue }) => {
        const value = getValue();
        if (!value) return <span className="text-secondary-text">-</span>;
        const colorClass = getWorkOrderPriorityColorClass(value as WorkOrderPriority);
        const icon =
          value === WorkOrderPriority.Critical ? (
            <WarningIcon size={20} className="text-default-red" />
          ) : (
            <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', colorClass)} />
          );
        return (
          <div className="flex items-center gap-[5px]">
            {icon}
            <span className="text-sm capitalize">{value}</span>
          </div>
        );
      },
      meta: {
        filterType: 'multi-select',
        getFilterOptionsFn: async () => {
          return Object.values(WorkOrderPriority).map((value) => ({
            label: value.charAt(0).toUpperCase() + value.slice(1),
            value,
          }));
        },
      },
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return <span>{formatDateWithUserTimezone(new Date(value), userTimezone)}</span>;
      },
    }),
    columnHelper.accessor('number', {
      header: 'ID',
      cell: ({ getValue }) => {
        const value = getValue();
        return <span>{EntityRefUtil.createWorkOrderRef(value)}</span>;
      },
    }),
    columnHelper.accessor('asset', {
      header: 'Asset',
      getGroupingValue: (row) => (row.asset ? EntityRefUtil.createAssetLabel(row.asset) : 'None'),
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{EntityRefUtil.createAssetLabel(value)}</span> : <span className="text-secondary-text">-</span>;
      },
      meta: {
        columnGroup: ASSET_SECTION_LABEL,
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetAssets({ limit: 100, ...filters })).json();

          const resultsMap = new Map(data.map((asset) => [asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingAssets } = await (
                await ApiService.GetAssets({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const asset of missingAssets) {
                resultsMap.set(asset.id, { label: EntityRefUtil.createAssetLabel(asset), value: asset.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    createAssetTagsColumn(columnHelper),
    columnHelper.accessor('title', {
      header: 'Title',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span className="block max-w-xs truncate">{value}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    columnHelper.accessor('location', {
      header: 'Location',
      getGroupingValue: (row) => row.location?.name || 'None',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value.name}</span> : <span className="text-secondary-text">-</span>;
      },
      enableGrouping: true,
      enableColumnFilter: true,
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetLocations({ limit: 100, ...filters })).json();

          const resultsMap = new Map(data.map((location) => [location.id, { label: location.name, value: location.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingLocations } = await (
                await ApiService.GetLocations({
                  limit: 100,
                  filters: { fields: { id: missingIds } },
                })
              ).json();

              for (const location of missingLocations) {
                resultsMap.set(location.id, { label: location.name, value: location.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      getGroupingValue: (row) => WorkOrderStatus[row.status] || 'None',
      cell: ({ getValue }) => {
        const status = getValue();

        const badgeClasses = match(status as WorkOrderStatus)
          .with(WorkOrderStatus.Open, () => 'bg-grey-1 text-default-text')
          .with(WorkOrderStatus.Closed, () => 'bg-default-light-green text-default-dark-green')
          .otherwise(() => 'bg-grey-1 text-default-text');

        const statusText = match(status)
          .with(WorkOrderStatus.Open, () => 'Open')
          .with(WorkOrderStatus.Closed, () => 'Closed')
          .otherwise(() => 'Void');

        const displayValue = (
          <Badge size="sm" className={badgeClasses}>
            {statusText}
          </Badge>
        );
        return displayValue;
      },
      meta: {
        cellClassName: 'w-[120px]',
        filterType: 'multi-select',
        // Status is always set on a work order — "-" filter is not applicable.
        disableNullFilter: true,
        getFilterOptionsFn: async () => {
          return [
            { label: 'Open', value: WorkOrderStatus.Open.toString() },
            { label: 'Closed', value: WorkOrderStatus.Closed.toString() },
          ];
        },
      },
    }),
    columnHelper.display({
      id: 'issues',
      header: 'Issues',
      enableSorting: false,
      cell: ({ row }) => renderIssueBadges(row.original.issues ?? []),
      meta: { cellClassName: 'max-w-[400px]' },
    }),
    columnHelper.display({
      id: 'schedule-checks',
      header: 'Schedule Checks',
      enableSorting: false,
      cell: ({ row }) => renderCheckBadges(row.original.checks ?? []),
      meta: { cellClassName: 'max-w-[400px]' },
    }),
    columnHelper.accessor(
      (row) => {
        const issueCount = row.issues?.length || 0;
        const closedIssueCount = row.issues?.filter((issue) => issue.status === IssueStatus.Closed).length || 0;
        const checkCount = row.checks?.filter((check) => check.status !== CheckStatus.Void).length || 0;
        const checkCompletedCount = row.checks?.filter((check) => check.status === CheckStatus.Closed).length || 0;
        const total = issueCount + checkCount;
        return total > 0 ? (closedIssueCount + checkCompletedCount) / total : -1;
      },
      {
        id: 'progress',
        header: 'Progress',
        enableSorting: false,
        cell: ({ row }) => {
          const issueCount = row.original.issues?.length || 0;
          const closedIssueCount = row.original.issues?.filter((issue) => issue.status === IssueStatus.Closed).length || 0;
          const checkCount = row.original.checks?.filter((check) => check.status !== CheckStatus.Void).length || 0;
          const checkCompletedCount = row.original.checks?.filter((check) => check.status === CheckStatus.Closed).length || 0;
          const total = issueCount + checkCount;
          const completed = closedIssueCount + checkCompletedCount;
          const openIssueCount = issueCount - closedIssueCount;
          const openCheckCount = checkCount - checkCompletedCount;

          return <TaskProgress completed={completed} total={total} openIssueCount={openIssueCount} openCheckCount={openCheckCount} />;
        },
        meta: {
          cellClassName: 'min-w-[180px]',
        },
      },
    ),
    columnHelper.accessor('sitemateUsers', {
      id: 'sitemateUsers',
      header: WORK_ORDER_USERS_COLUMN_HEADER,
      enableSorting: false,
      enableColumnFilter: true,
      cell: ({ row }) => {
        const assignees = [
          ...(row.original.gearbeltContributors || []).map((user) => ({
            id: `contributor-${user.id}`,
            name: EntityRefUtil.createUserFullNameRef(user),
            firstName: user.firstName,
            avatarUrl: user.avatarUrl,
          })),
          ...(row.original.sitemateUsers || []).map((user) => ({
            id: `user-${user.id}`,
            name: EntityRefUtil.createUserFullNameRef(user),
            firstName: user.firstName,
            avatarUrl: user.avatarUrl,
          })),
          ...(row.original.contacts || []).map((contact) => ({
            id: `contact-${contact.id}`,
            name: EntityRefUtil.createUserFullNameRef(contact),
            firstName: contact.firstName,
            avatarUrl: undefined as string | undefined,
          })),
        ];
        return assignees.length ? (
          <div className="flex flex-row items-center gap-2">
            {assignees.map((assignee) => (
              <UserAvatarLabel
                key={assignee.id}
                value={assignee.name}
                operatedByFullInitials={initials(assignee.name)}
                avatarUrl={assignee.avatarUrl || ''}
                avatarBackgroundColorClass={getAvatarBackgroundColorClass(assignee.firstName || '')}
                avatarSize="sm"
              />
            ))}
          </div>
        ) : (
          <span className="text-secondary-text">-</span>
        );
      },
      meta: {
        filterType: 'async-multi-select',
        getFilterOptionsFn: async (inputValue, value) => {
          const filters = inputValue ? { filters: { search: inputValue } } : undefined;
          const { data } = await (await ApiService.GetUsers({ limit: 100, filters: { ...filters?.filters, userType: 'sitemate-users' } })).json();

          const resultsMap = new Map(data.map((user) => [user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id }]));

          if (value?.length) {
            const missingIds = Array.isArray(value) ? value.filter((id) => !resultsMap.has(id)) : [];
            if (missingIds.length) {
              const { data: missingUsers } = await (await ApiService.GetUsers({ limit: 100, filters: { id: missingIds, userType: 'sitemate-users' } })).json();
              for (const user of missingUsers) {
                resultsMap.set(user.id, { label: EntityRefUtil.createUserFullNameRef(user), value: user.id });
              }
            }
          }

          return Array.from(resultsMap.values());
        },
      },
    }),
    columnHelper.accessor((row) => row.user, {
      id: 'createdBy',
      header: 'Created By',
      enableSorting: false,
      cell: ({ row }) => {
        const user = row.original.user;
        if (!user) return <span className="text-secondary-text">-</span>;
        const fullName = EntityRefUtil.createUserFullNameRef(user);
        const avatarBgClass = getAvatarBackgroundColorClass(user.firstName || '');
        return (
          <UserAvatarLabel
            value={fullName || '-'}
            operatedByFullInitials={initials(fullName)}
            avatarUrl={user.avatarUrl || ''}
            avatarBackgroundColorClass={avatarBgClass}
          />
        );
      },
    }),
    columnHelper.accessor('dueDate', {
      header: 'Due Date',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{formatDateWithUserTimezone(new Date(value), userTimezone)}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    columnHelper.accessor('po', {
      header: 'PO',
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span>{value}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    columnHelper.accessor('instructions', {
      header: 'Instructions',
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue();
        return value ? <span className="block max-w-xs truncate">{value}</span> : <span className="text-secondary-text">-</span>;
      },
    }),
    createTagsColumn(columnHelper, { header: 'Work Order Tags', entity: TagEntity.WorkOrder, variant: 'sky' }),
  ].filter(Boolean) as AccessorKeyColumnDef<GetWorkOrdersResponseDTO['data'][number]>[];
};

export const columns = createWorkOrdersTableColumns();

export const groupOptions = [
  { label: 'None', accessor: '' },
  ...columns
    .filter((column) => ['asset', 'location', 'status'].includes(column.accessorKey))
    .map((column) => ({ label: column.header?.toString() || '', accessor: column.accessorKey })),
] satisfies { label: string; accessor: string }[];

export const FILTER_FIELD_MAP: Record<
  NonNullable<ReturnType<typeof createWorkOrdersTableColumns>[number]['id'] | ReturnType<typeof createWorkOrdersTableColumns>[number]['accessorKey']>,
  keyof NonNullable<NonNullable<GetWorkOrdersRequestDTO['filters']>['fields']>
> = {
  status: 'status',
  asset: 'assetIds',
  assetTags: 'assetTagIds',
  location: 'locationIds',
  sitemateUsers: 'sitemateUserIds',
  contact: 'contactIds',
  priority: 'priority',
  tags: 'tagIds',
};
